/**
 * InlineError - Reusable error display component for use within pages
 *
 * Usage:
 *   <InlineError
 *     error={error}
 *     onRetry={() => fetchData()}
 *     message="Failed to load data"
 *   />
 */
function InlineError({ error, onRetry, message, style }) {
    if (!error) return null;

    // Determine error type and message
    let displayMessage = message || 'An error occurred';
    let errorType = 'error';

    if (error.response) {
        const status = error.response.status;
        if (status === 502 || status === 503 || status === 504) {
            displayMessage = 'Server is temporarily unavailable. Please try again.';
            errorType = 'server';
        } else if (status === 401) {
            displayMessage = 'Please log in to continue.';
            errorType = 'auth';
        } else if (status === 403) {
            displayMessage = 'You don\'t have permission to access this.';
            errorType = 'auth';
        } else if (status === 404) {
            displayMessage = 'The requested resource was not found.';
            errorType = 'notfound';
        } else if (status >= 500) {
            displayMessage = 'Server error. Please try again later.';
            errorType = 'server';
        }
    } else if (error.message?.includes('Network')) {
        displayMessage = 'Unable to connect. Check your internet connection.';
        errorType = 'network';
    }

    const bgColor = errorType === 'server' || errorType === 'network'
        ? 'rgba(239, 68, 68, 0.1)'
        : errorType === 'auth'
            ? 'rgba(255, 193, 7, 0.1)'
            : 'rgba(239, 68, 68, 0.1)';

    const borderColor = errorType === 'server' || errorType === 'network'
        ? 'rgba(239, 68, 68, 0.3)'
        : errorType === 'auth'
            ? 'rgba(255, 193, 7, 0.3)'
            : 'rgba(239, 68, 68, 0.3)';

    const textColor = errorType === 'auth' ? '#ffc107' : '#ef4444';

    return (
        <div style={{
            padding: '20px',
            background: bgColor,
            border: `1px solid ${borderColor}`,
            borderRadius: '12px',
            textAlign: 'center',
            ...style,
        }}>
            <p style={{
                margin: '0 0 16px',
                color: textColor,
                fontWeight: 600,
            }}>
                {displayMessage}
            </p>
            {onRetry && (
                <button
                    onClick={onRetry}
                    style={{
                        padding: '10px 20px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                        color: '#000',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                    }}
                >
                    Try Again
                </button>
            )}
        </div>
    );
}

export default InlineError;
